import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Camera, Lightbulb, Power, ShieldCheck, Thermometer, Wifi } from "lucide-react";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { confirmDeviceAction, executeDeviceAction } from "@/services/devices";

const deviceKind = (device) => String(device.device_type || device.type || "").toLowerCase();
const POWER_TOGGLE_KINDS = new Set(["light", "tv", "socket", "speaker", "fan", "plug"]);
const temperatureLabel = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  return Number.isFinite(Number(value)) ? `${Number(value).toFixed(1)} °C` : "—";
};
const motionLabel = (value) => (value === true ? "Bewegung erkannt" : value === false ? "Keine Bewegung" : "Bewegungsstatus —");

export const DevicePanel = ({ devices, token, onDeviceUpdate }) => {
  const [pendingId, setPendingId] = useState(null);
  const [error, setError] = useState("");
  const [confirmation, setConfirmation] = useState(null);
  const flat = useMemo(() => {
    const rooms = devices?.rooms || [];
    return rooms.flatMap((room) => (room.devices || []).map((device) => ({ ...device, roomName: room.name })));
  }, [devices]);

  const runDeviceAction = async (device, action, fallbackMessage) => {
    setPendingId(device.id);
    setError("");
    try {
      const updated = await executeDeviceAction(device.id, action, token);
      onDeviceUpdate?.(updated);
    } catch (actionError) {
      if (actionError?.code === "ACTION_CONFIRMATION_REQUIRED" && actionError?.actionId) {
        setConfirmation({
          actionId: actionError.actionId,
          deviceId: device.id,
          deviceName: device.name,
          message: actionError.message,
        });
        return;
      }
      setError(actionError.message || fallbackMessage);
    } finally {
      setPendingId(null);
    }
  };

  const toggleDevice = async (device) => {
    await runDeviceAction(
      device,
      { state: device.state?.power === "on" ? "off" : "on" },
      "Aktion fehlgeschlagen",
    );
  };

  const setBrightness = async (device, brightness) => {
    await runDeviceAction(
      device,
      { brightness, state: "on" },
      "Helligkeit konnte nicht aktualisiert werden",
    );
  };

  const setThermostatTarget = async (device, targetTemperature) => {
    await runDeviceAction(
      device,
      {
        target_temperature: targetTemperature,
        ...(device.state?.power ? { state: device.state.power } : {}),
      },
      "Solltemperatur konnte nicht aktualisiert werden",
    );
  };

  const confirmPendingDeviceAction = async () => {
    if (!confirmation?.actionId || !confirmation?.deviceId) return;
    setPendingId(confirmation.deviceId);
    setError("");
    try {
      const updated = await confirmDeviceAction(confirmation.actionId, token);
      setConfirmation(null);
      onDeviceUpdate?.(updated);
    } catch (actionError) {
      setError(actionError.message || "Bestätigung der Geräteaktion fehlgeschlagen");
    } finally {
      setPendingId(null);
    }
  };

  return (
    <>
      <section className="jarvis-panel" data-testid="device-panel">
        <div className="jarvis-panel-header">
          <div>
            <p className="jarvis-eyebrow">Live-Geräteübersicht</p>
            <h2 className="jarvis-title" data-testid="device-panel-title">Geräte</h2>
          </div>
          <div className="jarvis-chip" data-testid="device-panel-chip">{flat.length} Geräte</div>
        </div>
        {error ? <div className="jarvis-inline-error" data-testid="device-panel-error">{error}</div> : null}
        <div className="jarvis-list" data-testid="device-panel-list">
          {flat.slice(0, 12).map((device) => {
            const kind = deviceKind(device);
            const canTogglePower = POWER_TOGGLE_KINDS.has(kind);
            const actionDisabled = pendingId === device.id || device.trust_status === "REVOKED" || device.connection_state === "offline";
            return (
              <div className={`jarvis-list-row aion-device-control-row aion-device-control-row-${kind}`} key={device.id} data-testid={`device-panel-row-${device.id}`}>
                <div className="aion-device-control-main">
                  <div className="aion-device-control-icon" data-device-kind={kind}>
                    {kind === "light" ? <Lightbulb size={20} aria-hidden="true" /> : kind === "camera" ? <Camera size={20} aria-hidden="true" /> : kind === "thermostat" ? <Thermometer size={20} aria-hidden="true" /> : <Power size={20} aria-hidden="true" />}
                  </div>
                  <div className="aion-device-control-copy">
                    <strong>{device.name}</strong>
                    <small data-testid={`device-panel-meta-${device.id}`}>{device.roomName} · {device.device_type || device.type} · {(device.protocol || device.provider || "local").toUpperCase()}</small>
                    <div className="aion-device-control-flags">
                      <span><Wifi size={12} aria-hidden="true" /> {device.connection_state === "offline" ? "Offline" : "Online"}</span>
                      <span><ShieldCheck size={12} aria-hidden="true" /> {device.trust_status === "REVOKED" ? "Gesperrt" : "Verifiziert"}</span>
                    </div>
                  </div>
                </div>
                <div className="jarvis-device-actions aion-device-control-actions" data-testid={`device-panel-actions-${device.id}`}>
                  <span className={`aion-device-state-chip ${device.state?.power === "on" ? "is-on" : ""}`} data-testid={`device-panel-state-${device.id}`}>{device.state?.power === "on" ? "AN" : device.state?.power === "off" ? "AUS" : device.state?.power || "—"}</span>
                  <span className="aion-device-meta-chip" data-testid={`device-panel-health-${device.id}`}>{device.health_state === "HEALTHY" ? "OK" : device.health_state || "OK"}</span>
                  <span className="aion-device-meta-chip" data-testid={`device-panel-trust-${device.id}`}>{device.trust_status === "TRUSTED" ? "VERTRAUT" : device.trust_status || "VERTRAUT"}</span>
                  {kind === "thermostat" ? (
                    <div className="jarvis-brightness-row" data-testid={`device-panel-thermostat-controls-${device.id}`}>
                      <span className="jarvis-chip" data-testid={`device-current-temperature-${device.id}`}>Ist {temperatureLabel(device.state?.current_temperature ?? device.state?.temperature)}</span>
                      <span className="jarvis-chip" data-testid={`device-target-temperature-${device.id}`}>Soll {temperatureLabel(device.state?.target_temperature)}</span>
                      <Button size="sm" variant="outline" disabled={actionDisabled} onClick={() => setThermostatTarget(device, 19)} data-testid={`device-thermostat-19-${device.id}`}>19 °C</Button>
                      <Button size="sm" variant="outline" disabled={actionDisabled} onClick={() => setThermostatTarget(device, 21)} data-testid={`device-thermostat-21-${device.id}`}>21 °C</Button>
                      <Button size="sm" variant="outline" disabled={actionDisabled} onClick={() => setThermostatTarget(device, 23)} data-testid={`device-thermostat-23-${device.id}`}>23 °C</Button>
                      <small>Sollwert läuft über AIONs zentralen Trusted-Action-Pfad und bleibt in dieser Oberfläche auf sichere Sollwerte begrenzt.</small>
                    </div>
                  ) : null}
                  {kind === "camera" ? (
                    <div className="jarvis-brightness-row" data-testid={`device-panel-camera-readonly-${device.id}`}>
                      <span className="jarvis-chip" data-testid={`device-camera-connection-${device.id}`}>{device.connection_state || "unknown"}</span>
                      <span className="jarvis-chip" data-testid={`device-camera-motion-${device.id}`}>{motionLabel(device.state?.motion)}</span>
                      <small>Nur Status · kein erfundenes Livebild.</small>
                    </div>
                  ) : null}
                  {kind === "light" ? (
                    <div className="jarvis-brightness-row" data-testid={`device-panel-brightness-${device.id}`}>
                      <Button className="aion-brightness-button" size="sm" variant="outline" disabled={actionDisabled} onClick={() => setBrightness(device, 25)} data-testid={`device-brightness-low-${device.id}`}>25%</Button>
                      <Button className="aion-brightness-button" size="sm" variant="outline" disabled={actionDisabled} onClick={() => setBrightness(device, 70)} data-testid={`device-brightness-mid-${device.id}`}>70%</Button>
                    </div>
                  ) : null}
                  {canTogglePower ? (
                    <Button className={`aion-device-power-button ${device.state?.power === "on" ? "is-on" : ""}`} size="sm" disabled={actionDisabled} onClick={() => toggleDevice(device)} data-testid={`device-toggle-button-${device.id}`}>
                      <Power size={15} aria-hidden="true" />
                      {pendingId === device.id ? "Lädt…" : device.state?.power === "on" ? "Ausschalten" : "Einschalten"}
                    </Button>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <AlertDialog open={Boolean(confirmation)} onOpenChange={(open) => { if (!open && !pendingId) setConfirmation(null); }}>
        <AlertDialogContent
          className="aion-confirm-dialog overflow-hidden border-white/70 bg-background/95 shadow-[0_36px_90px_rgba(28,25,23,0.22)] backdrop-blur-xl sm:rounded-[28px]"
          data-testid="device-action-confirmation-dialog"
        >
          <AlertDialogHeader className="relative z-10">
            <AlertDialogTitle>Geräteaktion bestätigen?</AlertDialogTitle>
            <AlertDialogDescription>
              AION hat die Aktion für {confirmation?.deviceName || "dieses Gerät"} vor der Ausführung angehalten. Die zentrale Permission- und Human-Key-Prüfung verlangt deine ausdrückliche Bestätigung. Erst danach wird Scope und Aktion erneut geprüft und ausgeführt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {confirmation?.message ? <div className="aion-note" data-testid="device-action-confirmation-message">{confirmation.message}</div> : null}
          <AlertDialogFooter className="relative z-10">
            <AlertDialogCancel disabled={Boolean(pendingId)} data-testid="device-action-confirmation-cancel-button">Abbrechen</AlertDialogCancel>
            <Button
              onClick={() => void confirmPendingDeviceAction()}
              disabled={Boolean(pendingId)}
              data-testid="device-action-confirmation-confirm-button"
            >
              {pendingId ? "Prüft …" : "Aktion bestätigen"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
